import { Link } from '@tanstack/react-router';
import { OUTBOX_STATUS_LABELS_HE, type OutboxStatus } from '@nw/shared';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { Icon, type IconName } from './icon';

/** Small builders ported from mockups/assets/core.js — same classes (styles/app.css). */

const SEV: Record<string, [string, string, string]> = {
  CRITICAL: ['critical', 'קריטי', 'Critical'],
  HIGH: ['high', 'גבוה', 'High'],
  MEDIUM: ['medium', 'בינוני', 'Medium'],
  LOW: ['low', 'נמוך', 'Low'],
};

/** Hebrew name of a severity (קריטי / גבוה / בינוני / נמוך). */
export function sevName(level: string): string {
  return (SEV[level?.toUpperCase()] ?? SEV.LOW!)[1];
}

export function Sev({ level }: { level: string }) {
  const s = SEV[level?.toUpperCase()] ?? SEV.LOW!;
  return <span className={`sev ${s[0]}`}>{s[1]}</span>;
}

const STATUS_TONE: Record<OutboxStatus, string> = {
  queued: '',
  sending: 'info',
  sent: 'info',
  delivered: 'ok',
  read: 'ok',
  failed: 'danger',
  blocked: 'warn',
  cancelled: '',
  unknown: 'warn',
  held: 'warn',
};

export function MsgStatus({ status }: { status: OutboxStatus }) {
  return (
    <span className={`pill ${STATUS_TONE[status] ?? ''}`}>
      <span className="d" />
      {OUTBOX_STATUS_LABELS_HE[status] ?? status}
    </span>
  );
}

export function Pill({ tone = '', children }: { tone?: '' | 'ok' | 'warn' | 'danger' | 'info'; children: ReactNode }) {
  return (
    <span className={`pill ${tone}`}>
      <span className="d" />
      {children}
    </span>
  );
}

export function PageHead(props: {
  title: ReactNode;
  crumbs?: [string, string?][];
  after?: ReactNode;
  actions?: ReactNode;
  lead?: ReactNode;
}) {
  return (
    <>
      {props.crumbs ? (
        <nav className="crumbs" aria-label="מיקום">
          {props.crumbs.map(([label, to], i) => (
            <span key={i} style={{ display: 'contents' }}>
              {i ? <span aria-hidden="true">/</span> : null}
              {to ? <Link to={to}>{label}</Link> : <span>{label}</span>}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="page-head">
        <h1>{props.title}</h1>
        {props.after}
        {props.actions ? <div className="head-actions">{props.actions}</div> : null}
        {props.lead ? <p className="lead">{props.lead}</p> : null}
      </div>
    </>
  );
}

export function EmptyState(props: { icon?: IconName; title: ReactNode; text: ReactNode; action?: ReactNode }) {
  return (
    <div className="panel">
      <div className="empty">
        <div className="art">
          <Icon name={props.icon ?? 'sparkle'} />
        </div>
        <h2>{props.title}</h2>
        <p>{props.text}</p>
        {props.action}
      </div>
    </div>
  );
}

/** W2-2-1: shown above settings a read-only user can see but not change (the controls are disabled). */
export function ReadOnlyNote({ text = 'צפייה בלבד: אין לך הרשאה לשנות את ההגדרות האלה' }: { text?: string }) {
  return (
    <div className="callout info small">
      <Icon name="shield" />
      <div>{text}</div>
    </div>
  );
}

export function Skeleton() {
  return (
    <div className="content" aria-busy="true">
      <div className="skel" style={{ width: 120, height: 14, marginBottom: 10 }} />
      <div className="skel" style={{ width: 280, height: 28, marginBottom: 22 }} />
      <div className="skel" style={{ height: 86, marginBottom: 16 }} />
      <div className="grid cols-2">
        <div className="skel" style={{ height: 260 }} />
        <div className="skel" style={{ height: 260 }} />
      </div>
    </div>
  );
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal with the mockup markup (.overlay > .modal). Esc and backdrop click close it. The dialog is named by its title,
 * Tab / Shift+Tab cycle inside it, and focus returns to the element that opened it.
 */
export function Modal(props: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // keep the latest onClose without re-running the focus effect on every render (inline handlers)
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  useEffect(() => {
    if (!props.open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = ref.current;
      if (!root) return;
      const active = document.activeElement;
      // a modal stacked on top of this one owns the keyboard
      if (active && active !== document.body && !root.contains(active)) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, textarea, .modal-foot .btn')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener?.isConnected) opener.focus();
    };
  }, [props.open]);
  if (!props.open) return null;
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && props.onClose()}>
      <div className={`modal${props.wide ? ' wide' : ''}${props.className ? ` ${props.className}` : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <div className="modal-head">
          <h2 id={titleId}>{props.title}</h2>
          <span className="spacer" />
          <button className="btn ghost sm" onClick={props.onClose} aria-label="סגירה">
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{props.children}</div>
        <div className="modal-foot">
          {props.footer ?? (
            <button className="btn" onClick={props.onClose}>
              סגירה
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
